import { useMemo, useEffect } from "react"
import { useCubeQuery } from "@cubejs-client/react"
import { format } from "date-fns"
import useCurrentCompany from "~/ui-rtk/hooks/current-company"
import dayjs from "dayjs"
import { polyfillCubeQueryWithTimeDimension } from "~/ui-rtk/utils/cube"
import { type Query as TCubeQuery } from "@cubejs-client/core"

const CHART_COLORS = [
  "#5C8BD6",
  "#4F5295",
  "#98B26B",
  "#EE8EB9",
  "#66DECD",
  "#8161C9",
  "#FB6514",
  "#F4D148",
  "#C33FA3",
  "#599F5F",
  "#bc5441",
  "#21cad6",
  "#542501",
  "#5d15d8",
  "#f2ab21",
  "#d5196d",
  "#abcb73",
]

const getBarColor = (index: number): string =>
  CHART_COLORS[index % CHART_COLORS.length]

interface ResultRow {
  "company_brand_comparison.company_name": string
  "company_brand_comparison.brand_score": string | number
  "company_brand_comparison.md_date": string
  "company_brand_comparison.category": string
  "company_brand_comparison.model": string
}

export interface CompanyInfo {
  name: string
  ai_generated: boolean
  color: string
  score: string | number
}

export interface ChartDataPoint {
  date: string
  companies: {
    [key: string]: {
      score: number
      ai_generated: boolean
      color: string
    }
  }
}

interface UseBrandComparisonResult {
  categories: string[]
  filteredData: ChartDataPoint[]
  avgBrandScore: number
  isLoading: boolean
  currentCompanyName: string
}

export const useBrandComparison = (
  selectedCategory: string,
  onInitialCategory: (category: string) => void,
  hideAICompanies: boolean,
  dateRange?: [Date, Date],
): UseBrandComparisonResult => {
  const currentCompany = useCurrentCompany()

  // Memoize currentCompanyName to prevent unnecessary re-renders
  const currentCompanyName = useMemo(
    () => currentCompany?.name.toLowerCase() || "",
    [currentCompany?.name],
  )

  // Memoize competitors array to prevent unnecessary re-renders
  const competitors = useMemo(
    () =>
      [
        ...(currentCompany?.metadata?.keywords?.google?.competitors || []),
        currentCompanyName,
      ].filter(Boolean),
    [
      currentCompany?.metadata?.keywords?.google?.competitors,
      currentCompanyName,
    ],
  )

  const baseQuery = useMemo<TCubeQuery>(
    () => ({
      measures: [],
      dimensions: [
        "company_brand_comparison.company_name",
        "company_brand_comparison.brand_score",
        "company_brand_comparison.model",
        "company_brand_comparison.md_date",
        "company_brand_comparison.category",
      ],
      filters: [
        {
          dimension: "company_brand_comparison.category",
          operator: "set",
        },
      ],
      order: [["company_brand_comparison.md_date", "asc"]],
      timeDimensions: [
        {
          dimension: "company_brand_comparison.md_date",
          granularity: "month",
        },
      ],
    }),
    [],
  )

  const cubeQueryConfig = useMemo(
    () =>
      polyfillCubeQueryWithTimeDimension(baseQuery, {
        dateRange: [
          dayjs(dateRange?.[0]).toDate(),
          dayjs(dateRange?.[1]).toDate(),
        ],
      }),
    [baseQuery, dateRange],
  )

  const { resultSet, isLoading } = useCubeQuery(cubeQueryConfig)

  // Calculate categories once when data changes
  const categories = useMemo(() => {
    if (!resultSet) return []
    const rows = resultSet.tablePivot() as unknown as ResultRow[]
    return [
      ...new Set(rows.map(row => row["company_brand_comparison.category"])),
    ].filter(Boolean)
  }, [resultSet])

  // Set initial category
  useEffect(() => {
    if (!selectedCategory && categories.length > 0) {
      onInitialCategory(categories[0])
    }
  }, [categories, selectedCategory, onInitialCategory])

  return useMemo(() => {
    if (!resultSet) {
      return {
        categories,
        filteredData: [],
        avgBrandScore: 0,
        currentCompanyName,
        isLoading,
      }
    }

    const rows = resultSet?.tablePivot() as unknown as ResultRow[]

    // Group data by date and company, calculating average scores for multiple models
    const groupedByDate = rows.reduce<
      Record<string, Record<string, { sum: number; count: number }>>
    >((acc, row) => {
      const date = row["company_brand_comparison.md_date"]
      const company = row["company_brand_comparison.company_name"].toLowerCase()
      const category = row["company_brand_comparison.category"]
      const score = Number(row["company_brand_comparison.brand_score"]) || 0

      // Skip invalid scores
      if (isNaN(score)) {
        return acc
      }

      if (category === selectedCategory) {
        if (!acc[date]) {
          acc[date] = {}
        }
        if (!acc[date][company]) {
          acc[date][company] = { sum: 0, count: 0 }
        }
        acc[date][company].sum += score
        acc[date][company].count += 1
      }

      return acc
    }, {})

    // Create chart data with averaged scores
    const chartData: ChartDataPoint[] = Object.entries(groupedByDate).map(
      ([date, companyData]) => {
        const companiesWithInfo = Object.entries(companyData)
          .filter(
            ([name]) =>
              !hideAICompanies ||
              competitors.map(c => c.toLowerCase()).includes(name),
          )
          .sort(([a, _], [b, __]) => {
            if (a.toLowerCase() === currentCompanyName.toLowerCase()) return -1
            if (b.toLowerCase() === currentCompanyName.toLowerCase()) return 1
            return 0
          })
          .map(([name, { sum, count }], index) => ({
            name,
            score: count > 0 ? Number((sum / count).toFixed(4)) : 0, // Round to 4 decimal places
            ai_generated: !competitors
              .map(c => c.toLowerCase())
              .includes(name.toLowerCase()),
            color: getBarColor(index),
          }))

        return {
          date: format(new Date(date), "MMM yyyy"),
          companies: companiesWithInfo.reduce(
            (acc, company) => ({
              ...acc,
              [company.name]: {
                score: company.score,
                ai_generated: company.ai_generated,
                color: company.color,
              },
            }),
            {},
          ),
        }
      },
    )

    // Calculate average score using the averaged company scores
    const validScores = chartData.flatMap(point =>
      Object.values(point.companies)
        .map(company => Number(company.score))
        .filter(
          score => !isNaN(score) && score !== null && score !== undefined,
        ),
    )

    const avgScore = validScores.length
      ? Number(
          (
            validScores.reduce((sum, score) => sum + score, 0) /
            validScores.length
          ).toFixed(2),
        )
      : 0

    return {
      categories,
      filteredData: chartData,
      avgBrandScore: avgScore,
      currentCompanyName,
      isLoading,
    }
  }, [
    resultSet,
    selectedCategory,
    currentCompanyName,
    isLoading,
    competitors,
    hideAICompanies,
    categories,
  ])
}
